import { EventBus }     from '../../core/EventBus.js';
import { serverIntent } from '../../core/serverIntent.js';
import { GameState }    from '../../core/GameState.js';
import { Modal }        from '../components/Modal.js';
import { formatMoney }  from '../../utils/formatting.js';
import { showAlert, showConfirm } from '../components/dialogs.js';

const TIER_LABELS = {
  junior: 'Junior',
  senior: 'Senior',
  expert: 'Expert'
};

const TIER_CLASSES = {
  junior: 'tier-junior',
  senior: 'tier-senior',
  expert: 'tier-expert'
};

export class StaffModal {
  constructor({ staffSystem }) {
    this.staffSystem = staffSystem;
    this.modal = new Modal('staff-modal', { title: 'Staff Management' });

    EventBus.on('ui:staffOpen', () => this.open());
  }

  open() {
    this._render();
    this.modal.open();
  }

  _render() {
    const managers = [...GameState.routeManagers.values()];
    const pool = GameState.staffCandidatePool || [];
    const totalDailySalary = managers.reduce((sum, m) => sum + m.dailySalary, 0);

    // Hired managers section
    let hiredHtml = '';
    if (managers.length > 0) {
      hiredHtml = managers.map(m => {
        const route = m.assignedRouteId ? GameState.tradeRoutes.get(m.assignedRouteId) : null;
        return `<div class="staff-card">
          <div class="staff-card-header">
            <strong>${m.name}</strong>
            <span class="tier-badge ${TIER_CLASSES[m.skillTier]}">${TIER_LABELS[m.skillTier]}</span>
          </div>
          <div class="staff-card-body">
            <span>Salary: ${formatMoney(m.dailySalary)}/day</span>
            <span>${route ? `Route: ${route.name}` : 'Unassigned'}</span>
          </div>
          <button class="btn btn-sm btn-danger" data-fire="${m.id}">Fire</button>
        </div>`;
      }).join('');
    } else {
      hiredHtml = '<p class="panel-empty">No managers hired yet.</p>';
    }

    // Available to hire section
    let poolHtml = '';
    if (pool.length > 0) {
      poolHtml = pool.map(m => {
        return `<div class="staff-card staff-card-candidate">
          <div class="staff-card-header">
            <strong>${m.name}</strong>
            <span class="tier-badge ${TIER_CLASSES[m.skillTier]}">${TIER_LABELS[m.skillTier]}</span>
          </div>
          <div class="staff-card-body">
            <span>Salary: ${formatMoney(m.dailySalary)}/day (${formatMoney(m.dailySalary * 30)}/month)</span>
          </div>
          <button class="btn btn-sm btn-primary" data-hire="${m.id}">Hire</button>
        </div>`;
      }).join('');
    } else {
      poolHtml = '<p class="panel-empty">No candidates available. Check back later.</p>';
    }

    this.modal.setBody(`
      <div class="staff-summary">
        <span>${managers.length} manager${managers.length !== 1 ? 's' : ''} hired</span>
        <span>Total salary: ${formatMoney(totalDailySalary)}/day (${formatMoney(totalDailySalary * 30)}/month)</span>
      </div>

      <div class="staff-section">
        <h4>Hired Managers</h4>
        <div class="staff-list">${hiredHtml}</div>
      </div>

      <div class="staff-section">
        <h4>Available to Hire</h4>
        <button class="btn btn-sm" id="btn-refresh-pool">Refresh Candidates</button>
        <div class="staff-list">${poolHtml}</div>
      </div>`);

    // Fire handlers
    this.modal.el.querySelectorAll('[data-fire]').forEach(btn => {
      btn.addEventListener('click', () => {
        const managerId = btn.dataset.fire;
        const mgr = GameState.routeManagers.get(managerId);
        if (mgr) {
          showConfirm(`Fire ${mgr.name}?${mgr.assignedRouteId ? ' Their route will be paused.' : ''}`, { title: 'Fire Manager', confirmLabel: 'Fire' }).then(async ok => {
            if (!ok) return;
            await serverIntent('staff/fire-manager', { managerId });
            this._render();
          });
        }
      });
    });

    // Hire handlers
    this.modal.el.querySelectorAll('[data-hire]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const managerId = btn.dataset.hire;
        const result = await serverIntent('staff/hire-manager', { managerId });
        if (!result.success) { return; }
        this._render();
      });
    });

    // Refresh pool
    const refreshBtn = document.getElementById('btn-refresh-pool');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        // Pool is managed server-side; re-render to show current state
        this._render();
      });
    }
  }
}
